import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { OrderFormat, ServiceTimeKindType } from '@ancon/wildcat-types'
import getOutletOrderFormats from '@ancon/wildcat-utils/outlet/getOutletOrderFormats'
import { getOutletNextReadyToOrderTime } from '@ancon/wildcat-utils/outlet'

import { OrderFormatUrlSlug } from '../../app/constants'
import {
  outletFiltersOrderFormatSelector,
  outletListItemSelector,
} from '../store/outletSelector'
import useAppSelector from '../../../store/hooks/useAppSelector'
import useAppDispatch from '../../../store/hooks/useAppDispatch'
import {
  outletsSetSelectedOutletListItem,
  outletsSetVisibleOrderFormatSelectionModal,
} from '../../outlets/store/outletsSlice'
import { validateTableOrderUrl } from '../store/outletThunks'
import {
  outletSetIsVisibleServiceTimeKindSelectionModal,
  outletSetOutletFilters,
} from '../store/outletSlice'
import localStorageUtils from '../../app/utils/localStorageUtils'
import { LocalStorageCartMeta } from '../../checkout/types'
import useAppStore from '../../../store/hooks/useAppStore'
import { checkoutIsSystemProductSelector } from '../../checkout/store/checkoutSelectors'
import hasOutletDetailsURLFlag from '../utils/hasOutletDetailsURLFlag'
import isOutletInPreOrderOnlyMode from '../utils/isOutletInPreOrderOnlyMode'

function isValidServiceTimeParam(serviceTime: string) {
  const date = new Date(serviceTime)
  return !Number.isNaN(date.getTime())
}

export default function TableOrderingFilter() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const store = useAppStore()
  const dispatch = useAppDispatch()
  const outlet = useAppSelector(outletListItemSelector)
  const orderFormats = useMemo(
    () => (outlet ? getOutletOrderFormats(outlet) : []),
    [outlet],
  )

  const isSystemProductCheckout = useAppSelector(
    checkoutIsSystemProductSelector,
  )

  const { id: outletId } = outlet || {}
  const {
    orderFormat,
    tableId,
    serviceTime: queryServiceTime,
  } = router?.query || {}
  const hasQueryServiceTime = queryServiceTime !== undefined

  useEffect(() => {
    const checkout = localStorageUtils.getItem<LocalStorageCartMeta>('cartMeta')

    if (checkout?.outletId !== outlet?.id || isSystemProductCheckout) {
      const orderFormatNumber = /^[0-9]*$/.test(orderFormat as string)
        ? Number(orderFormat)
        : (OrderFormatUrlSlug[orderFormat as keyof typeof OrderFormatUrlSlug] ??
          undefined)
      const isAvailableOrderFormat =
        orderFormatNumber && orderFormats.includes(orderFormatNumber)

      const hasOutletDetailsFlag = hasOutletDetailsURLFlag(searchParams)

      if (!orderFormat || !isAvailableOrderFormat) {
        if (orderFormats.length === 1) {
          const isTableOrder = orderFormats[0] === OrderFormat.TableOrder

          const isPreOrderOnly =
            !isTableOrder && isOutletInPreOrderOnlyMode(outlet)

          if (isPreOrderOnly && !hasOutletDetailsFlag) {
            dispatch(outletSetIsVisibleServiceTimeKindSelectionModal(true))
          }
          dispatch(
            outletSetOutletFilters({
              orderFormat: orderFormats[0],
              ...(isPreOrderOnly && {
                serviceTime: {
                  kind: ServiceTimeKindType.AtSpecifiedTime,
                  time: getOutletNextReadyToOrderTime(
                    outlet!,
                    orderFormats[0],
                  ).toISOString(),
                },
              }),
            }),
          )
        } else {
          // When go back click after order format selected
          const selectedOrderFormat = outletFiltersOrderFormatSelector(
            store.getState(),
          )

          dispatch(outletsSetSelectedOutletListItem(outlet))
          if (!selectedOrderFormat && !hasOutletDetailsFlag) {
            dispatch(outletsSetVisibleOrderFormatSelectionModal(true))
          }
        }
      } else if (orderFormatNumber === OrderFormat.TableOrder && tableId) {
        dispatch(
          validateTableOrderUrl({
            outletId: outletId as string,
            tableId: tableId as string,
          }),
        )
      } else {
        const isValidServiceTime = isValidServiceTimeParam(
          queryServiceTime as string,
        )

        const isTableOrder = orderFormatNumber === OrderFormat.TableOrder
        const isPreOrderOnly =
          !isTableOrder && isOutletInPreOrderOnlyMode(outlet)

        if (isPreOrderOnly && !queryServiceTime && !hasOutletDetailsFlag) {
          dispatch(outletSetIsVisibleServiceTimeKindSelectionModal(true))
        }

        dispatch(
          outletSetOutletFilters({
            orderFormat: orderFormatNumber,
            ...(hasQueryServiceTime && {
              serviceTime: {
                kind: queryServiceTime
                  ? ServiceTimeKindType.AtSpecifiedTime
                  : ServiceTimeKindType.AsSoonAsPossible,
                time: isValidServiceTime ? (queryServiceTime as string) : '',
              },
            }),
            ...(isPreOrderOnly && {
              serviceTime: {
                kind: ServiceTimeKindType.AtSpecifiedTime,
                time: isValidServiceTime
                  ? (queryServiceTime as string)
                  : getOutletNextReadyToOrderTime(
                      outlet!,
                      orderFormatNumber,
                    ).toISOString(),
              },
            }),
          }),
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderFormats,
    dispatch,
    orderFormat,
    outlet,
    tableId,
    outletId,
    queryServiceTime,
    hasQueryServiceTime,
    store,
    isSystemProductCheckout,
  ])

  return null
}
